function roleId(role) {
  return typeof role === 'string' ? role : role?.id;
}

function safeHttpUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function mergeTextOverrideMap(previous = {}, next = {}) {
  const merged = { ...previous };
  for (const [key, value] of Object.entries(next)) {
    if (!value) continue;
    merged[key] = typeof value === 'object' && !Array.isArray(value)
      ? { ...(previous[key] || {}), ...value }
      : value;
  }
  return merged;
}

export function createJourneyOverlay(template, genericJourney, existing = {}) {
  const roleKeys = (genericJourney?.roles || []).map(roleId).filter(Boolean);
  const usefulLinks = genericJourney?.usefulLinks || [];
  const stages = genericJourney?.stages || template?.stages || [];

  return {
    templateId: template.id,
    included: existing.included !== false,
    status: existing.status || 'draft',
    title: existing.title || template.title,
    serviceName: existing.serviceName || 'Your hospital or service',
    branding: {
      trustName: existing.branding?.trustName || 'Your Trust',
      logoUrl: existing.branding?.logoUrl || '',
      logoAlt: existing.branding?.logoAlt || 'Your Trust logo',
    },
    roleOverrides: {
      ...Object.fromEntries(roleKeys.map((key) => [key, existing.roleOverrides?.[key] !== false])),
      ...(existing.roleOverrides || {}),
    },
    linkOverrides: {
      ...Object.fromEntries(usefulLinks.map((link) => [link.id, {
        title: existing.linkOverrides?.[link.id]?.title || link.title || '',
        description: existing.linkOverrides?.[link.id]?.description || link.description || '',
        url: existing.linkOverrides?.[link.id]?.url || '',
        visible: existing.linkOverrides?.[link.id]?.visible !== false,
      }])),
      ...(existing.linkOverrides || {}),
    },
    stageOverrides: {
      ...Object.fromEntries(stages.map((stage) => [stage.id, {
        title: existing.stageOverrides?.[stage.id]?.title || stage.title,
        description: existing.stageOverrides?.[stage.id]?.description || stage.description || '',
      }])),
      ...(existing.stageOverrides || {}),
    },
    clinicalReviews: existing.clinicalReviews && typeof existing.clinicalReviews === 'object' ? existing.clinicalReviews : {},
    contentComment: existing.contentComment || '',
    updatedAt: existing.updatedAt || null,
  };
}

export function applyJourneyOverlay(genericJourney, overlay) {
  if (!genericJourney || !overlay) return genericJourney;
  const roles = (genericJourney.roles || []).filter((role) => overlay.roleOverrides?.[roleId(role)] !== false);

  const linkTextOverrides = {};
  const usefulLinks = (genericJourney.usefulLinks || []).map((link) => {
    const configured = overlay.linkOverrides?.[link.id] || {};
    const configuredTitle = configured.title;
    const configuredDescription = configured.description;
    const titleChanged = Boolean(configuredTitle && configuredTitle !== link.title);
    const descriptionChanged = Boolean(configuredDescription && configuredDescription !== link.description);
    if (titleChanged || descriptionChanged) {
      linkTextOverrides[link.id] = {
        ...(titleChanged ? { title: true } : {}),
        ...(descriptionChanged ? { description: true } : {}),
      };
    }
    return {
      ...link,
      title: configuredTitle || link.title,
      description: configuredDescription || link.description,
      url: safeHttpUrl(configured.url) || null,
      visible: configured.visible !== false,
    };
  });

  const stageTextOverrides = {};
  const stages = (genericJourney.stages || []).map((stage) => {
    const configured = overlay.stageOverrides?.[stage.id];
    const titleChanged = Boolean(configured?.title && configured.title !== stage.title);
    const descriptionChanged = Boolean(configured?.description && configured.description !== stage.description);
    if (titleChanged || descriptionChanged) {
      stageTextOverrides[stage.id] = {
        ...(titleChanged ? { title: true } : {}),
        ...(descriptionChanged ? { description: true } : {}),
      };
    }
    return {
      ...stage,
      title: configured?.title || stage.title,
      description: configured?.description || stage.description,
      // A Trust-admin role exclusion must also apply to the stage people cards,
      // not just the Journey-wide team. Other stage role assignments are kept.
      ...(Array.isArray(stage.roleIds) ? {
        roleIds: stage.roleIds.filter((role) => overlay.roleOverrides?.[roleId(role)] !== false),
      } : {}),
    };
  });

  const titleChanged = Boolean(overlay.title && overlay.title !== genericJourney.title);
  const previousTextOverrides = genericJourney.localTextOverrides || {};
  const localTextOverrides = {
    ...previousTextOverrides,
    title: Boolean(previousTextOverrides.title || titleChanged),
    usefulLinks: mergeTextOverrideMap(previousTextOverrides.usefulLinks, linkTextOverrides),
    stages: mergeTextOverrideMap(previousTextOverrides.stages, stageTextOverrides),
  };

  return {
    ...genericJourney,
    trustName: overlay.branding?.trustName || genericJourney.trustName,
    hospitalName: overlay.serviceName || genericJourney.hospitalName,
    logoUrl: safeHttpUrl(overlay.branding?.logoUrl) || null,
    logoAlt: overlay.branding?.logoAlt || genericJourney.logoAlt,
    logoPlaceholder: !safeHttpUrl(overlay.branding?.logoUrl),
    title: overlay.title || genericJourney.title,
    status: overlay.status || genericJourney.status,
    roles,
    usefulLinks,
    stages,
    localTextOverrides,
  };
}

export function countJourneyOverlayChanges(genericJourney, overlay) {
  if (!genericJourney || !overlay) return 0;
  let count = 0;
  if (overlay.title && overlay.title !== genericJourney.title) count += 1;
  if (overlay.serviceName && overlay.serviceName !== genericJourney.hospitalName) count += 1;
  if (overlay.branding?.trustName && overlay.branding.trustName !== genericJourney.trustName) count += 1;
  if (overlay.branding?.logoUrl) count += 1;
  if (overlay.contentComment) count += 1;
  count += (genericJourney.roles || []).filter((role) => overlay.roleOverrides?.[roleId(role)] === false).length;
  count += (genericJourney.usefulLinks || []).filter((link) => {
    const next = overlay.linkOverrides?.[link.id];
    return Boolean(next?.url)
      || next?.visible === false
      || Boolean(next?.title && next.title !== link.title)
      || Boolean(next?.description && next.description !== link.description);
  }).length;
  count += (genericJourney.stages || []).filter((stage) => {
    const next = overlay.stageOverrides?.[stage.id];
    return next && (next.title !== stage.title || (next.description || '') !== (stage.description || ''));
  }).length;
  return count;
}

export function compactJourneyOverlay(genericJourney, overlay) {
  if (!genericJourney || !overlay) return null;
  const compact = { templateId: overlay.templateId };
  if (overlay.title && overlay.title !== genericJourney.title) compact.title = overlay.title;
  if (overlay.serviceName && overlay.serviceName !== genericJourney.hospitalName) compact.serviceName = overlay.serviceName;
  if (overlay.status && overlay.status !== genericJourney.status) compact.status = overlay.status;

  const branding = {};
  if (overlay.branding?.trustName && overlay.branding.trustName !== genericJourney.trustName) branding.trustName = overlay.branding.trustName;
  if (overlay.branding?.logoUrl) branding.logoUrl = safeHttpUrl(overlay.branding.logoUrl);
  if (overlay.branding?.logoAlt && overlay.branding.logoAlt !== genericJourney.logoAlt) branding.logoAlt = overlay.branding.logoAlt;
  if (Object.values(branding).some(Boolean)) compact.branding = branding;

  const roleOverrides = Object.fromEntries(
    (genericJourney.roles || [])
      .map(roleId)
      .filter((key) => key && overlay.roleOverrides?.[key] === false)
      .map((key) => [key, false]),
  );
  if (Object.keys(roleOverrides).length) compact.roleOverrides = roleOverrides;

  const linkOverrides = {};
  for (const link of genericJourney.usefulLinks || []) {
    const next = overlay.linkOverrides?.[link.id];
    if (!next) continue;
    const titleChanged = next.title && next.title !== link.title;
    const descriptionChanged = next.description && next.description !== link.description;
    const url = safeHttpUrl(next.url);
    const hidden = next.visible === false;
    if (titleChanged || descriptionChanged || url || hidden) {
      linkOverrides[link.id] = {
        ...(titleChanged ? { title: next.title } : {}),
        ...(descriptionChanged ? { description: next.description } : {}),
        ...(url ? { url } : {}),
        ...(hidden ? { visible: false } : {}),
      };
    }
  }
  if (Object.keys(linkOverrides).length) compact.linkOverrides = linkOverrides;

  const stageOverrides = {};
  for (const stage of genericJourney.stages || []) {
    const next = overlay.stageOverrides?.[stage.id];
    if (!next) continue;
    const titleChanged = next.title && next.title !== stage.title;
    const descriptionChanged = next.description && next.description !== stage.description;
    if (titleChanged || descriptionChanged) stageOverrides[stage.id] = { ...(titleChanged ? { title: next.title } : {}), ...(descriptionChanged ? { description: next.description } : {}) };
  }
  if (Object.keys(stageOverrides).length) compact.stageOverrides = stageOverrides;
  return compact;
}
